import { storedFiltersToQuery } from '@/features/sliced-form/mapping/profile-to-query'
import {
  DEFAULT_TICKER_STATS_FILTER_PROFILES,
  DEFAULT_TICKER_STATS_FILTER_PROFILE_ID,
} from '@/features/sliced-form/schemas/profile-presets'
import type { QueryFilterGroup } from '@/services/api/scanner-types'
import { z } from 'zod'
import { AggregateProfileSchema } from '@/features/sliced-form/schemas/aggregate-entity'
import type { AggregateProfile } from '@/features/sliced-form/schemas/aggregate-entity'
import {
  DEFAULT_AGGREGATE_PROFILES,
  DEFAULT_AGGREGATE_PROFILE_ID,
} from '@/features/sliced-form/schemas/profile-presets'

// ─── Normalized Chart Point ───

export const NormalizedChartPointSchema = z
  .object({
    timestamp: z.string(), // 'HH:mm'
    average: z.number(),
  })
  .passthrough() // allows dynamic run keys like 'YYYY-MM-DD_TICKER'
export type NormalizedChartPoint = z.infer<typeof NormalizedChartPointSchema>

// ─── Aggregate Stats API ───

export const AggregateStatsRequestSchema = z.object({
  aggregates: z.array(z.record(z.unknown())),
  filters: z.record(z.unknown()).default({}),
  adjusted: z.boolean().default(false),
  avg_price_chart: z.object({
    agg: z.enum(['AVG', 'MED']),
    only: z.boolean(),
  }),
  /**
   * False returns the stats without the normalized chart, so the cheap stats
   * SELECT and the expensive chart query can run as two parallel requests.
   * Mutually exclusive with `avg_price_chart.only` (the backend 400s).
   */
  include_chart: z.boolean().optional(),
})
export type AggregateStatsRequest = z.infer<typeof AggregateStatsRequestSchema>

export const AggregateStatsResponseSchema = z.object({
  stats: z.record(z.string(), z.unknown()),
  chart: z.array(NormalizedChartPointSchema),
})
export type AggregateStatsResponse = z.infer<typeof AggregateStatsResponseSchema>

// ─── History Records API ───

export const HistoryTagFilterSchema = z.object({
  include: z.array(z.number()).default([]),
  exclude: z.array(z.number()).default([]),
})
export type HistoryTagFilter = z.infer<typeof HistoryTagFilterSchema>

/** Shape-similarity condition sent as `pattern_filter` (mirrors backend PatternSearchRequest). */
export const HistoryPatternFilterSchema = z.object({
  window: z.string(),
  ticker: z.string().optional(),
  trading_day: z.string().optional(),
  query_vector: z.array(z.number()).optional(),
  k: z.number().optional(),
  min_price: z.number().optional(),
  max_price: z.number().optional(),
  min_gap_pct: z.number().optional(),
  max_gap_pct: z.number().optional(),
  min_dollar_vol: z.number().optional(),
  min_match_score: z.number().optional(),
})
export type HistoryPatternFilter = z.infer<typeof HistoryPatternFilterSchema>

export const RecordsRequestSchema = z.object({
  columns: z.array(z.record(z.unknown())),
  filters: z.record(z.unknown()).default({}),
  order: z.array(z.record(z.unknown())).default([]),
  offset: z.number(),
  limit: z.number(),
  adjusted: z.boolean().default(false),
  tag_filter: HistoryTagFilterSchema.optional(),
  pattern_filter: HistoryPatternFilterSchema.optional(),
  search: z.string().optional(),
})
export type RecordsRequest = z.infer<typeof RecordsRequestSchema>

/** Backend `meta.pattern_warning` — why a shape search couldn't score (no corpus shape, too broad). */
export const PatternWarningSchema = z.object({
  code: z.string(),
  message: z.string(),
})
export type PatternWarning = z.infer<typeof PatternWarningSchema>

export const RecordsResponseSchema = z.object({
  data: z.array(z.record(z.unknown())),
  has_more: z.boolean(),
  total_count: z.number(),
  /** Present when a pattern search fell back to unscored (blank Match column) — surfaced to the user. */
  pattern_warning: PatternWarningSchema.optional(),
})
export type RecordsResponse = z.infer<typeof RecordsResponseSchema>

// ─── Chart Aggregates API ───

/** Value-cohort binning spec — a `group_by` that bins a numeric column into bands. */
export const BucketSpecSchema = z.object({
  column: z.string(),
  edges: z.array(z.number()),
  unbound_left: z.boolean().optional(),
  unbound_right: z.boolean().optional(),
  labels: z.array(z.string()).optional(),
})

/**
 * Intraday time-of-day binning spec — a `group_by` that bins a wall-clock TIME
 * column (e.g. `day0_high_time`) into fixed intervals from the session open.
 * Mirrors the backend `TimeOfDayBucketSpec` (`models/requests.py`).
 */
export const TimeOfDayGroupBySchema = z.object({
  column: z.string(),
  bin_minutes: z.number(),
  open_time: z.string(),
})

/**
 * Boolean-condition second dimension — splits each primary group true/false by a
 * row-level condition (a FilterClause tree, the same shape as `filters`). Mirrors
 * the backend `ConditionBreakdown`; the condition uses the full condition builder.
 */
export const ConditionBreakdownSchema = z.object({
  condition: z.record(z.unknown()),
  true_label: z.string().optional(),
  false_label: z.string().optional(),
})
export type ConditionBreakdown = z.infer<typeof ConditionBreakdownSchema>

/**
 * EDITABLE breakdown value held in panel state. A column name (string), or a
 * condition draft (the raw condition entities the builder edits + labels). The
 * panel converts a draft to a backend `ConditionBreakdown` at fetch time — the
 * same profile-shape vs query-shape split filters use.
 */
export interface ConditionBreakdownDraft {
  conditions: Record<string, unknown>[]
  trueLabel?: string
  falseLabel?: string
}
export type BreakdownValue = string | ConditionBreakdownDraft

/**
 * Named cohorts: a `group_by` that labels each row by the FIRST condition it
 * meets ("gap up 15%+" vs "gap down 15%+"), over any columns. Mirrors the backend
 * `CaseGroupSpec` (`models/requests.py`). Rows come back keyed `bucket`, in case
 * order, exactly like a `BucketSpec`.
 */
export const CaseGroupBySchema = z.object({
  cases: z.array(z.object({ label: z.string(), condition: z.record(z.unknown()) })),
  /** Label for rows meeting no case; `null` leaves them out of every cohort. */
  else_label: z.string().nullable().optional(),
})
export type CaseGroupBy = z.infer<typeof CaseGroupBySchema>

/**
 * EDITABLE named cohorts held in panel state: the same profile-shape vs
 * query-shape split as {@link ConditionBreakdownDraft}. Each cohort's conditions
 * are the raw entities the condition builder edits; the panel converts to a
 * backend {@link CaseGroupBy} at fetch time (`resolveBackendCases`).
 */
export interface CaseGroupDraft {
  cases: Array<{ label: string; conditions: Record<string, unknown>[] }>
  elseLabel?: string | null
}

export const ChartAggregatesRequestSchema = z.object({
  aggregates: z.array(z.record(z.unknown())),
  filters: z.record(z.unknown()).default({}),
  // A column/time-token string, a value-cohort BucketSpec, a TimeOfDayGroupBy
  // (intraday time-of-day bucket) object, or named cohorts (CaseGroupBy).
  group_by: z
    .union([z.string(), BucketSpecSchema, TimeOfDayGroupBySchema, CaseGroupBySchema])
    .optional(),
  // Optional second grouping: a column, a boolean ConditionBreakdown, named
  // cohorts, or value bands. The backend takes all four (`requests.py`:
  // `str | ConditionBreakdown | CaseGroupSpec | BucketSpec`); the Playbook's
  // heat grid is the first caller to cross two banded dimensions.
  breakdown: z
    .union([z.string(), ConditionBreakdownSchema, CaseGroupBySchema, BucketSpecSchema])
    .optional(),
  adjusted: z.boolean().default(false),
  // Row cap for grouped results (backend default 5000, max 50000). Only the
  // finest groupings (a point per ticker-day) need to raise it.
  limit: z.number().int().positive().max(50000).optional(),
  // Sort direction over the grouping columns; 'desc' keeps the latest groups
  // when the limit truncates. Backend default is 'asc'.
  order: z.enum(['asc', 'desc']).optional(),
  // Tag memberships resolved by the backend (same field as /v2/history/columns).
  tag_filter: HistoryTagFilterSchema.optional(),
})
export type ChartAggregatesRequest = z.infer<typeof ChartAggregatesRequestSchema>

export const ChartAggregatesResponseSchema = z.object({
  data: z.array(z.record(z.unknown())),
  has_more: z.boolean().optional(),
  total_count: z.number().optional(),
})
export type ChartAggregatesResponse = z.infer<typeof ChartAggregatesResponseSchema>

// ─── Panel State ───

export const TickerStatsPanelStateSchema = z
  .object({
    currentTab: z.enum(['stats', 'records']).optional(),
    filters: z.union([z.record(z.unknown()), z.array(z.unknown())]).optional(),
    selectedColumns: z.array(z.string()).optional(),
    aggregateProfile: AggregateProfileSchema.optional(),
    hiddenLegendItems: z.array(z.string()).optional(),
    avgPriceAggregate: z.enum(['AVG', 'MED']).optional(),
    filterProfileId: z.string().nullable().optional(),
    aggregateProfileId: z.string().nullable().optional(),
    columnProfileId: z.string().nullable().optional(),
    /** Stat-card row above the aggregates table (toggled from the panel menu). */
    showCards: z.boolean().optional(),
    /** One aggregate id (or null) per card slot; resolved against the profile on read. */
    cardSlots: z.array(z.string().nullable()).optional(),
    recordsOrderBy: z.string().optional(),
    recordsOrder: z.enum(['asc', 'desc']).optional(),
    gridColumnSizeKey: z.string().nullable().optional(),
    columnWidths: z.record(z.number()).optional(),
    columnSizeMode: z
      .enum(['fitToData', 'fitToHeaders', 'fitToPanel', 'manual'])
      .transform(
        (v) => (v === 'fitToHeaders' ? 'fitToData' : v) as 'fitToData' | 'fitToPanel' | 'manual',
      )
      .optional(),
    columnWrapHeaders: z.boolean().optional(),
  })
  .passthrough()
export type TickerStatsPanelState = z.infer<typeof TickerStatsPanelStateSchema>

// ─── Bar Chart Columns ───

export const BAR_CHART_COLUMNS = [
  { label: 'PMH %', key: 'day0_pm_high_pct' },
  { label: 'Gap %', key: 'day0_gap_pct' },
  { label: 'High %', key: 'day0_close_high_pct' },
  { label: 'Close %', key: 'day0_chg_pct' },
] as const

// ─── Default Aggregate Profile ───

/**
 * Default aggregate profile shown when no profile is saved: the built-in
 * "General Stats" preset. Falls back to a hardcoded Default only if the preset
 * is somehow unavailable (it's a static const, so this shouldn't happen).
 */
export function createDefaultStatsProfile(): AggregateProfile {
  const generalStats = DEFAULT_AGGREGATE_PROFILES.find((p) => p.id === DEFAULT_AGGREGATE_PROFILE_ID)
  if (generalStats) return structuredClone(generalStats.aggregateProfile)

  return {
    name: 'Default',
    aggregates: [
      {
        id: 'd1_count',
        conditionalAgg: 'CNT',
        label: 'Count',
        description: '',
        target: { column: null, type: 'column' },
        conditions: [],
      },
      {
        id: 'd1_gap',
        conditionalAgg: 'AVG',
        label: 'Gap',
        description: '',
        target: { column: 'day0_gap', type: 'column' },
        conditions: [],
      },
      {
        id: 'd1_gap_pct',
        conditionalAgg: 'AVG',
        label: 'Gap %',
        description: '',
        target: { column: 'day0_gap_pct', type: 'column' },
        conditions: [],
      },
      {
        id: 'd1_high_spike',
        conditionalAgg: 'AVG',
        label: 'High Spike %',
        description: '',
        target: { column: 'day0_close_high_pct', type: 'column' },
        conditions: [],
      },
      {
        id: 'd1_low_spike',
        conditionalAgg: 'AVG',
        label: 'Low Spike %',
        description: '',
        target: { column: 'day0_close_low_pct', type: 'column' },
        conditions: [],
      },
      {
        id: 'd1_range',
        conditionalAgg: 'AVG',
        label: 'Range',
        description: '',
        target: { column: 'day0_range', type: 'column' },
        conditions: [],
      },
      {
        id: 'd1_volume',
        conditionalAgg: 'AVG',
        label: 'Volume',
        description: '',
        target: { column: 'day0_vol', type: 'column' },
        conditions: [],
      },
    ],
    groups: [
      {
        label: 'Day 1',
        data: [
          'd1_count',
          'd1_gap',
          'd1_gap_pct',
          'd1_high_spike',
          'd1_low_spike',
          'd1_range',
          'd1_volume',
        ],
      },
    ],
  }
}

/**
 * The Ticker Stats default preset translated to query shape: the population a stats panel
 * queries when it has no persisted `filters` (mirrors the scanner's DEFAULT_SCANNER_FILTERS).
 */
export const DEFAULT_STATS_FILTERS: QueryFilterGroup = storedFiltersToQuery(
  DEFAULT_TICKER_STATS_FILTER_PROFILES.find((p) => p.id === DEFAULT_TICKER_STATS_FILTER_PROFILE_ID)!
    .filters,
)
